import React, { createContext, useContext, useState, useEffect } from 'react';

const ShopifyAuthContext = createContext();

export const useShopifyAuth = () => {
  const context = useContext(ShopifyAuthContext);
  if (!context) {
    throw new Error('useShopifyAuth must be used within a ShopifyAuthProvider');
  }
  return context;
};

export const ShopifyAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load saved session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('shopify_access_token');
    const tokenExpiry = localStorage.getItem('shopify_token_expiry');
    
    if (savedToken && tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      if (expiryDate > new Date()) {
        setAccessToken(savedToken);
        fetchProfile(savedToken);
      } else {
        // Token expired, clear it
        localStorage.removeItem('shopify_access_token');
        localStorage.removeItem('shopify_token_expiry');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch customer profile
  const fetchProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/customer/profile?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('shopify_access_token');
        localStorage.removeItem('shopify_token_expiry');
        setAccessToken(null);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  // Register new customer
  const register = async (email, password, firstName, lastName, phone = null) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          phone,
          acceptsMarketing: true
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Save token
      if (data.accessToken) {
        localStorage.setItem('shopify_access_token', data.accessToken);
        localStorage.setItem('shopify_token_expiry', data.expiresAt);
        setAccessToken(data.accessToken);
        setCustomer(data.customer);
      }

      return { success: true, customer: data.customer };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Login customer
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Save token
      localStorage.setItem('shopify_access_token', data.accessToken);
      localStorage.setItem('shopify_token_expiry', data.expiresAt);
      setAccessToken(data.accessToken);
      setCustomer(data.customer);

      return { success: true, customer: data.customer };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Logout customer
  const logout = async () => {
    try {
      if (accessToken) {
        await fetch(`${API_URL}/api/customer/logout?token=${accessToken}`, {
          method: 'POST',
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('shopify_access_token');
      localStorage.removeItem('shopify_token_expiry');
      setAccessToken(null);
      setCustomer(null);
    }
  };

  // Update profile
  const updateProfile = async (updates) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer/update?token=${accessToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Update failed');
      }

      // Refresh profile
      await fetchProfile(accessToken);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Add address
  const addAddress = async (address) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer/address?token=${accessToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(address),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to add address');
      }

      // Refresh profile to get updated addresses
      await fetchProfile(accessToken);
      return { success: true, address: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Password recovery
  const recoverPassword = async (email) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customer/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      return { success: true, message: data.message };
    } catch (err) {
      return { success: true, message: 'If an account exists, a password reset email has been sent.' };
    }
  };

  // Refresh profile
  const refreshProfile = async () => {
    if (accessToken) {
      await fetchProfile(accessToken);
    }
  };

  const value = {
    customer,
    accessToken,
    loading,
    error,
    isAuthenticated: !!customer,
    register,
    login,
    logout,
    updateProfile,
    addAddress,
    recoverPassword,
    refreshProfile,
  };

  return (
    <ShopifyAuthContext.Provider value={value}>
      {children}
    </ShopifyAuthContext.Provider>
  );
};
